import React, { useState, useEffect } from "react";
import { clientsAPI, exportAPI } from "../lib/api";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FileText } from "lucide-react";

export default function ReportsPage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await clientsAPI.list();
        setClients(res.data);
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };
    fetchClients();
  }, []);

  const parseBlobError = async (err) => {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        return json.detail || json.message || text;
      } catch {
        try {
          return await err.response.data.text();
        } catch {
          return err.message;
        }
      }
    }
    return err.response?.data?.detail || err.message;
  };

  const handleExport = async (type) => {
    const params = {};
    if (selectedClient !== "all") params.client_id = selectedClient;
    if (dateFrom) params.start_date = dateFrom;
    if (dateTo) params.end_date = dateTo;

    const exportPaths = {
      clients: "/export/clients",
      projects: "/export/projects",
      tasks: "/export/tasks",
      "time-entries": "/export/time-entries",
      invoices: "/export/invoices",
      quotes: "/export/quotes",
      payments: "/export/payments",
      leads: "/export/leads",
      contacts: "/export/contacts",
    };

    const path = exportPaths[type];
    if (!path) return;

    try {
      const response = await api.get(path, {
        params,
        responseType: "blob",
      });

      if (!response.data || response.data.size === 0) {
        toast.warning("No data available to export");
        return;
      }

      const today = new Date().toISOString().split("T")[0];
      const objectUrl = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${type}_export_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      a.remove();
      toast.success(`${type} exported successfully`);
    } catch (err) {
      const errorMsg = await parseBlobError(err);
      console.error("Export error:", err.response?.status, errorMsg);
      toast.error(`Export failed: ${errorMsg}`);
    }
  };

  const exportItems = [
    { type: "clients", label: "Clients", icon: FileSpreadsheet, description: "Export all client data" },
    { type: "projects", label: "Projects", icon: FileSpreadsheet, description: "Export projects with client filter" },
    { type: "tasks", label: "Tasks", icon: FileSpreadsheet, description: "Export tasks with filters" },
    { type: "time-entries", label: "Time Entries", icon: FileSpreadsheet, description: "Export time tracking data" },
    { type: "invoices", label: "Invoices", icon: FileSpreadsheet, description: "Export invoice records" },
    { type: "quotes", label: "Quotes", icon: FileSpreadsheet, description: "Export quote data" },
    { type: "payments", label: "Payments", icon: FileSpreadsheet, description: "Export payment records" },
    { type: "leads", label: "Leads", icon: FileSpreadsheet, description: "Export job applications" },
    { type: "contacts", label: "Contacts", icon: FileSpreadsheet, description: "Export contact information" },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports & Exports</h1>
        <p className="text-muted-foreground mt-1">
          Export your data to CSV for analysis and backup
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Export Filters</CardTitle>
          <CardDescription>
            Apply filters to narrow down your export data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="export-client-filter">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="export-date-from"
              />
            </div>
            <div className="space-y-2">
              <Label>Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="export-date-to"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {exportItems.map((item) => (
          <Card key={item.type} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{item.label}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full mt-4 gap-2"
                onClick={() => handleExport(item.type)}
                data-testid={`export-${item.type}-btn`}
              >
                <FileDown className="h-4 w-4" />
                Export CSV
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* PDF Export Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            PDF Export
          </CardTitle>
          <CardDescription>
            Generate print-ready PDF documents for quotes and invoices
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            To export a quote or invoice as PDF:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Navigate to the specific quote or invoice you want to export</li>
            <li>Click the "View" or "Print" button</li>
            <li>Use your browser's print function (Ctrl/Cmd + P)</li>
            <li>Select "Save as PDF" as the destination</li>
          </ol>
          <p className="text-sm text-muted-foreground mt-4">
            The quote/invoice view is optimized for printing with your business branding.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
